import { CacheKey, MemCacheService } from '@csp/csp-common-memcache';
import { ApiOptions, CspError, Maybe, User, UserId } from '@csp/csp-common-model';
import { AsyncLock } from '@csp/csp-common-util';
import { UserIncludeFieldType } from '@csp/dmdp-api-user-dto';
import { uniq } from 'lodash';
import { UserQuery } from '../model/UserQuery';
import { UserQueryService } from './UserQueryService';

const TTL_SECS = 20 * 60;

const createCacheKey = (userId: string, fields: UserIncludeFieldType[]): CacheKey => CacheKey.from([userId, ...fields]);

const asyncLock = AsyncLock.from<void>();

const byUserIds = async (
  userIds: UserId[],
  fields = UserQuery.defaultFields,
  apiOptions?: ApiOptions,
): Promise<User[]> => {
  const uniqueUserIds = uniq(userIds);

  const getCacheValue = (userId: string): Maybe<User> => MemCacheService.getValue<User>(createCacheKey(userId, fields));

  const cachedUsers: User[] = [];
  const missingUserIds: UserId[] = [];
  const pendingCacheKeys: UserId[] = [];
  uniqueUserIds.forEach(userId => {
    const cachedUser = getCacheValue(userId);
    if (cachedUser) {
      cachedUsers.push(cachedUser);
    } else if (asyncLock.isPending(createCacheKey(userId, fields))) {
      pendingCacheKeys.push(createCacheKey(userId, fields));
    } else {
      missingUserIds.push(userId);
    }
  });
  const missingCacheKeys = missingUserIds.map(userId => createCacheKey(userId, fields));

  if (missingUserIds.length) {
    asyncLock
      .acquire(missingCacheKeys, async (): Promise<void> => {
        const userPage = await UserQueryService.byUserIds(missingUserIds, fields, 5_000, undefined, apiOptions);

        userPage.users.forEach(user => {
          const cacheKey = createCacheKey(user.userId, fields);
          MemCacheService.setValue(cacheKey, TTL_SECS, user);
        });
      })
      .catch(e => {
        console.error(`Failed to aquire lock for ${missingCacheKeys}`, e);
      });
  }

  const remainingCacheKeys = [...missingCacheKeys, ...pendingCacheKeys];
  await asyncLock.waitForKeys(remainingCacheKeys);

  remainingCacheKeys.forEach(key => {
    const cachedUser = MemCacheService.getValue<User>(key);
    if (cachedUser) {
      cachedUsers.push(cachedUser);
    }
  });

  return cachedUsers;
};

const byUserId = async (userId: UserId, fields = UserQuery.defaultFields, apiOptions?: ApiOptions): Promise<User> => {
  const [user] = await byUserIds([userId], fields, apiOptions);

  if (user) {
    return user;
  } else {
    throw CspError.notFound(`User with id ${userId} not found`);
  }
};

export const CachedUserQueryService = {
  byUserIds,
  byUserId,
};
